import React, { useState } from 'react';

import { useRutilusQuery } from '../../../api/graphql/useRutilusQuery';
import { BiteTimeCard } from '../../../components/Cards/BiteTimeCard/BiteTimeCard';
import { InfoCard } from '../../../components/Cards/InfoCard/InfoCard';
import { Button } from '../../../components/Clickables/Buttons/Button';
import { ExternalLink } from '../../../components/Clickables/ExternalLink/ExternalLink';
import { Heading } from '../../../components/Heading/Heading';
import { ChevronDownIcon } from '../../../components/Icons/ChevronDownIcon';
import { ChevronUpIcon } from '../../../components/Icons/ChevronUpIcon';
import { IUCNStatusIcon } from '../../../components/Icons/IUCNStatusIcon';
import { Text } from '../../../components/Text/Text';
import { useGeolocation } from '../../../contexts/GeolocationContext/GeolocationContext';
import { convertLength } from '../../../helpers/conversion';
import { CurrentUser, useUserInfo } from '../../../helpers/hooks/useUserInfo';
import { getBiteTimeSummary } from '../../../helpers/misc/getBiteTimeSummary';
import { capitalize } from '../../../helpers/string';
import {
  GetBiteTimePreview,
  GetBiteTimePreviewVariables,
  GetSingleSpecies_entry as ISpecies,
} from '../../../interfaces/graphql';
import { getBiteTimePreview } from '../../../queries/rutilus/Species';
import styles from '../Species.module.css';

const FADE_THRESHOLD = 7;

interface IProps {
  species: ISpecies;
}

const relevantDataCategories = ['waterTypes', 'iucnStatus', 'threatToHumans'];

const getFullTextIUCNStatus = (iucnStatus: string): string => {
  switch (iucnStatus) {
    case 'LC':
      return 'Least concern';
    case 'NT':
      return 'Near threatened';
    case 'VU':
      return 'Vulnerable';
    case 'EN':
      return 'Endangered';
    case 'CR':
      return 'Critically endangered';
    case 'EW':
      return 'Extinct in the wild';
    case 'EX':
      return 'Extinct';
    case 'NE':
      return 'Not evaluated';
    case 'DD':
      return 'Data deficient';
    default:
      throw new Error(`Unhandled IUCN status: ${iucnStatus}`);
  }
};

const getThreatToHumans = (threat: string): string => {
  switch (threat) {
    case 'harmless':
      return 'Harmless';
    case 'poisonous_to_eat':
      return 'Poisonous to eat';
    case 'venomous':
      return 'Venomous';
    case 'potential_pest':
      return 'Potential pest';
    case 'reports_of_ciguatera_poisoning':
      return 'Reports of ciguatera poisoning';
    case 'traumatogenic':
      return 'Traumatogenic';
    default:
      throw new Error(`Unhandled threat type ${threat}`);
  }
};

const handleDepth = (
  minDepth: number | null,
  maxDepth: number | null,
  user: CurrentUser,
): string => {
  const min = minDepth ? convertLength(minDepth, user, true) : '?';
  const max = maxDepth ? convertLength(maxDepth, user, true) : '?';
  return `${min} - ${max}`;
};

const getNbrOfInfoCards = (species: ISpecies): number => {
  const hasDepth = species.minCommonDepth !== null || species.maxCommonDepth !== null;

  const categoryCount = Object.entries(species).reduce(
    (count, [key, value]) => {
      if (relevantDataCategories.includes(key) && value !== null && value !== undefined) {
        return count + 1;
      }
      return count;
    },
    hasDepth ? 1 : 0,
  );

  /* +1 represents the BiteTime card, which is not fetched via Graphql */
  return categoryCount + 1;
};

export const DetailedInformationSection = ({ species }: IProps) => {
  const { isLoading: geolocationLoading, location } = useGeolocation();
  const { data: biteTimeData } = useRutilusQuery<GetBiteTimePreview, GetBiteTimePreviewVariables>(
    getBiteTimePreview,
    {
      variables: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        ...location?.coordinates!,
        id: species._id,
      },
      skip: geolocationLoading,
    },
  );
  const { user } = useUserInfo();

  const nbrOfCards = getNbrOfInfoCards(species);
  const [isFaded, setIsFaded] = useState(nbrOfCards >= FADE_THRESHOLD);

  const { waterTypes, minCommonDepth, maxCommonDepth, iucnStatus, threatToHumans } = species;

  const biteTimeProps = biteTimeData
    ? getBiteTimeSummary(biteTimeData)
    : { timeframe: '', rating: 'Loading...' };

  return (
    <div data-testid="DetailedInformationSection">
      <Heading as="h2" variant="sm" className="mb-5 mt-0">
        Detailed Information
      </Heading>

      <div className={`${isFaded && styles.informationFade}`}>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4 md:grid-cols-3">
          <BiteTimeCard {...biteTimeProps} />
          {waterTypes && waterTypes.length > 0 && (
            <InfoCard title="Water type" iconText={waterTypes.map(capitalize).join(' / ')} />
          )}
          {maxCommonDepth !== null && minCommonDepth !== null ? (
            <InfoCard
              title="Common depth"
              iconText={handleDepth(minCommonDepth, maxCommonDepth, user)}
            />
          ) : (
            <></>
          )}
          {iucnStatus && (
            <InfoCard
              title="IUCN Status"
              iconText={getFullTextIUCNStatus(iucnStatus)}
              iconComponent={<IUCNStatusIcon status={iucnStatus} />}
              placeIconAboveIconText
            />
          )}
          {threatToHumans && threatToHumans.length > 0 ? (
            <InfoCard
              title="Threat to humans"
              iconText={threatToHumans.map(threat => getThreatToHumans(threat)).join(' / ')}
            />
          ) : (
            <></>
          )}
        </div>
        <Text className="mt-3" variant="xs">
          Source:{' '}
          <ExternalLink
            href={`https://www.fishbase.se/Summary/SpeciesSummary.php?ID=${species.legacyId}`}
          >
            Fishbase
          </ExternalLink>
        </Text>
      </div>

      {isFaded ? (
        <div className="-mt-32 mb-4 grid sm:hidden">
          <div
            className="col-span-full"
            style={{
              height: 130,
              background: 'linear-gradient(rgb(255, 255, 255, 0) 35%, rgba(255, 255, 255, 1) 100%)',
              gridColumn: 1,
              gridRow: 1,
            }}
          />
          <Button
            variant="textPrimary"
            className="col-span-full -mb-4 self-end font-heading focus:outline-none"
            onClick={() => {
              setIsFaded(false);
            }}
            style={{ gridColumn: 1, gridRow: 1 }}
          >
            See more
            <ChevronDownIcon />
          </Button>
        </div>
      ) : (
        <Button
          variant="textPrimary"
          className={`mx-auto mt-4 flex font-heading focus:outline-none ${
            nbrOfCards < FADE_THRESHOLD ? 'hidden' : 'sm:hidden'
          }`}
          onClick={() => {
            setIsFaded(true);
          }}
        >
          See less
          <ChevronUpIcon />
        </Button>
      )}
    </div>
  );
};
